// Swatch tooltip: desktop hover (Figma 8085-57356/69/82), mobile tap (8085-45822 dual / 46348 single)

const SWATCH_TOOLTIP_MOBILE_MAX = 749;

// Gap between a swatch and its anchored popup, and the minimum breathing room
// kept between the popup and the edges of the viewport / the fixed header.
const SWATCH_TOOLTIP_ANCHOR_GAP = 12;
const SWATCH_TOOLTIP_VIEWPORT_MARGIN = 8;

/**
 * Bottom edge, in viewport pixels, of the site header when it is pinned over
 * the top of the page (`#header-group` is `position: fixed`). An anchored
 * popup sits at z-index 9999, so without this it would be drawn on top of the
 * navigation instead of next to the swatch that opened it.
 *
 * @returns {number} Viewport y of the header's bottom edge, or 0 when the
 *   header is not overlaying the page.
 */
function swatchTooltipHeaderBottom() {
  const header = document.getElementById('header-group');
  if (!header) return 0;
  const { position } = window.getComputedStyle(header);
  if (position !== 'fixed' && position !== 'sticky') return 0;
  return Math.max(0, header.getBoundingClientRect().bottom);
}

/**
 * Document-space coordinates for a popup anchored to a swatch: horizontally
 * centred on the swatch, directly above it when there is room below the
 * header, flipped underneath it otherwise, and always clamped inside the
 * viewport. The popup is `pointer-events: none` on desktop, so overlapping the
 * product image above the swatch never blocks hovering the card.
 *
 * @param {DOMRect} anchorRect - Swatch rect in viewport coordinates.
 * @param {number} width - Popup width in px.
 * @param {number} height - Popup height in px.
 * @returns {{left: number, top: number}} Coordinates for an absolutely
 *   positioned popup appended to `document.body`.
 */
function swatchTooltipAnchoredPosition(anchorRect, width, height) {
  const scrollX = window.scrollX || 0;
  const scrollY = window.scrollY || 0;
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
  const viewportHeight = document.documentElement.clientHeight || window.innerHeight;

  const minTop = swatchTooltipHeaderBottom() + SWATCH_TOOLTIP_VIEWPORT_MARGIN;
  const maxTop = viewportHeight - height - SWATCH_TOOLTIP_VIEWPORT_MARGIN;
  const above = anchorRect.top - height - SWATCH_TOOLTIP_ANCHOR_GAP;
  const below = anchorRect.bottom + SWATCH_TOOLTIP_ANCHOR_GAP;

  let top = above >= minTop ? above : below;
  // Neither side fits (short viewport / tall popup): keep it on screen and
  // clear of the header rather than letting it run off the bottom.
  if (top > maxTop) top = Math.max(minTop, Math.min(above, maxTop));

  const centred = anchorRect.left + anchorRect.width / 2 - width / 2;
  const left = Math.max(
    SWATCH_TOOLTIP_VIEWPORT_MARGIN,
    Math.min(centred, viewportWidth - width - SWATCH_TOOLTIP_VIEWPORT_MARGIN)
  );

  return { left: left + scrollX, top: top + scrollY };
}

class SwatchTooltip {
  /** @type {HTMLElement | null} */
  #tooltip = null;
  /** @type {HTMLElement | null} */
  #backdrop = null;
  /** @type {HTMLElement | null} */
  #surface = null;
  /** @type {HTMLButtonElement | null} */
  #closeBtn = null;
  /** @type {HTMLButtonElement | null} */
  #ctaBtn = null;
  /** @type {HTMLElement | null} */
  #rowDual = null;
  /** @type {HTMLElement | null} */
  #rowSingle = null;
  /** @type {HTMLElement | null} */
  #desktopMedia = null;
  /** @type {HTMLImageElement | null} */
  #imgPrimary = null;
  /** @type {HTMLImageElement | null} */
  #imgSecondary = null;
  /** @type {HTMLImageElement | null} */
  #imgHero = null;
  /** @type {HTMLImageElement | null} */
  #imgDesktop = null;
  /** @type {HTMLElement | null} */
  #textEl = null;
  /** @type {HTMLElement | null} */
  #titleEl = null;
  /** @type {HTMLElement | null} */
  #descEl = null;
  /** @type {HTMLElement | null} */
  #toneRowEl = null;
  /** @type {HTMLElement | null} */
  #toneEl = null;
  /** @type {Element | null} */
  #lastSwatch = null;
  /** @type {(() => void) | null} */
  #boundDocTouch = null;
  /** @type {boolean} */
  #bodyScrollLocked = false;
  /** @type {string} */
  #savedBodyOverflow = '';
  /** @type {number | null} */
  #longPressTimer = null;
  /** @type {boolean} */
  #longPressFired = false;
  /** @type {number | null} */
  #hideAnimTimer = null;

  constructor() {
    this.#build();
    this.#bindEvents();
  }

  #build() {
    this.#tooltip = document.createElement('div');
    this.#tooltip.className = 'swatch-option-tooltip';
    this.#tooltip.setAttribute('role', 'dialog');
    this.#tooltip.setAttribute('aria-hidden', 'true');
    this.#tooltip.innerHTML = `
      <div class="swatch-option-tooltip__backdrop" aria-hidden="true"></div>
      <div class="swatch-option-tooltip__surface">
        <button type="button" class="swatch-option-tooltip__close">Close</button>
        <div class="swatch-option-tooltip__body">
          <div class="swatch-option-tooltip__media swatch-option-tooltip__media--desktop">
            <div class="swatch-option-tooltip__img-frame swatch-option-tooltip__img-frame--desktop">
              <img class="swatch-option-tooltip__img swatch-option-tooltip__img--desktop" src="" alt="" width="219" height="219" />
            </div>
          </div>
          <div class="swatch-option-tooltip__media swatch-option-tooltip__media--dual">
            <div class="swatch-option-tooltip__img-frame swatch-option-tooltip__img-frame--square">
              <img class="swatch-option-tooltip__img swatch-option-tooltip__img--primary" src="" alt="" width="271" height="271" />
            </div>
            <div class="swatch-option-tooltip__img-frame swatch-option-tooltip__img-frame--portrait">
              <img class="swatch-option-tooltip__img swatch-option-tooltip__img--secondary" src="" alt="" width="177" height="270" />
            </div>
          </div>
          <div class="swatch-option-tooltip__media swatch-option-tooltip__media--single">
            <div class="swatch-option-tooltip__img-frame swatch-option-tooltip__img-frame--hero">
              <img class="swatch-option-tooltip__img swatch-option-tooltip__img--hero" src="" alt="" width="219" height="219" />
            </div>
          </div>
          <div class="swatch-option-tooltip__text">
            <div class="swatch-option-tooltip__title"></div>
            <p class="swatch-option-tooltip__description"></p>
            <div class="swatch-option-tooltip__tone-row">
              <span class="swatch-option-tooltip__tone"></span>
            </div>
          </div>
        </div>
        <button type="button" class="swatch-option-tooltip__cta" data-show-example-trigger>click to see examples</button>
      </div>
    `;
    document.body.appendChild(this.#tooltip);
    this.#tooltip.style.display = 'none';

    this.#backdrop = this.#tooltip.querySelector('.swatch-option-tooltip__backdrop');
    this.#surface = this.#tooltip.querySelector('.swatch-option-tooltip__surface');
    this.#closeBtn = this.#tooltip.querySelector('.swatch-option-tooltip__close');
    this.#ctaBtn = this.#tooltip.querySelector('.swatch-option-tooltip__cta');
    this.#rowDual = this.#tooltip.querySelector('.swatch-option-tooltip__media--dual');
    this.#rowSingle = this.#tooltip.querySelector('.swatch-option-tooltip__media--single');
    this.#desktopMedia = this.#tooltip.querySelector('.swatch-option-tooltip__media--desktop');
    this.#imgPrimary = this.#tooltip.querySelector('.swatch-option-tooltip__img--primary');
    this.#imgSecondary = this.#tooltip.querySelector('.swatch-option-tooltip__img--secondary');
    this.#imgHero = this.#tooltip.querySelector('.swatch-option-tooltip__img--hero');
    this.#imgDesktop = this.#tooltip.querySelector('.swatch-option-tooltip__img--desktop');
    this.#textEl = this.#tooltip.querySelector('.swatch-option-tooltip__text');
    this.#titleEl = this.#tooltip.querySelector('.swatch-option-tooltip__title');
    this.#descEl = this.#tooltip.querySelector('.swatch-option-tooltip__description');
    this.#toneRowEl = this.#tooltip.querySelector('.swatch-option-tooltip__tone-row');
    this.#toneEl = this.#tooltip.querySelector('.swatch-option-tooltip__tone');

    this.#closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#hideTooltip();
    });
    this.#backdrop?.addEventListener('click', () => this.#hideTooltip());
    this.#ctaBtn?.addEventListener('click', () => {
      window.setTimeout(() => this.#hideTooltip(), 0);
    });
  }

  #isMobileViewport() {
    return window.matchMedia(`(max-width: ${SWATCH_TOOLTIP_MOBILE_MAX}px)`).matches;
  }

  #lockBodyScroll() {
    if (this.#bodyScrollLocked) return;
    this.#savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.#bodyScrollLocked = true;
  }

  #unlockBodyScroll() {
    if (!this.#bodyScrollLocked) return;
    document.body.style.overflow = this.#savedBodyOverflow;
    this.#bodyScrollLocked = false;
    this.#savedBodyOverflow = '';
  }

  #clearLongPress() {
    if (this.#longPressTimer !== null) {
      clearTimeout(this.#longPressTimer);
      this.#longPressTimer = null;
    }
  }

  #getValidAttribute(/** @type {Element | null} */ el, name) {
    const value = el?.getAttribute(name);
    if (!value || value === 'null' || value === 'undefined' || value.startsWith('Liquid error')) {
      return '';
    }
    return value;
  }

  /** @param {Element} el */
  #resolveSwatchButton(el) {
    if (el.classList.contains('color-swatch')) return el;
    if (el.matches('input')) {
      return el.closest('label')?.querySelector('.color-swatch') ?? null;
    }
    return null;
  }

  /** @param {Element} swatch */
  #pickerRoot(swatch) {
    return swatch.closest('[data-pdp-variant-swatch-picker]');
  }

  /** @param {Element} swatch */
  #toneLabelClass(toneRaw) {
    const t = (toneRaw || '').toLowerCase().trim();
    if (t === 'warm' || t === 'cool' || t === 'neutral') return t;
    return '';
  }

  /**
   * Point an <img> at a new src without flashing the previous one.
   * The element stays `visibility: hidden` until the new src has decoded.
   * @param {HTMLImageElement | null} img
   * @param {string} src
   * @param {boolean} shouldShow
   */
  #applyImg(img, src, shouldShow) {
    if (!img) return;
    img.onload = null;
    img.onerror = null;

    if (!shouldShow || !src) {
      img.removeAttribute('src');
      img.style.display = 'none';
      img.style.visibility = '';
      return;
    }

    img.style.display = '';

    // Same image already in place — show it right away, no reload.
    if (img.getAttribute('src') === src) {
      img.style.visibility = '';
      return;
    }

    // New image: hide pixels until it is ready, then reveal.
    img.style.visibility = 'hidden';
    const reveal = () => {
      img.style.visibility = '';
      img.onload = null;
      img.onerror = null;
    };
    img.onload = reveal;
    img.onerror = reveal;
    img.src = src;
    // Cached images may already be complete before onload binds.
    if (img.complete && img.naturalWidth > 0) reveal();
  }

  /** @param {Element} swatch */
  #populateFromSwatch(swatch) {
    // On collection page (swatch lives inside .color-swatch-picker, NOT
    // inside [data-pdp-variant-swatch-picker]) the rollover must show only
    // the colour example — never the product variant image.
    const isCollectionContext = Boolean(
      swatch.closest('.color-swatch-picker') && !swatch.closest('[data-pdp-variant-swatch-picker]')
    );

    const variantImg = isCollectionContext ? '' : this.#getValidAttribute(swatch, 'data-variant-image');
    const sampleImg = this.#getValidAttribute(swatch, 'data-sample-image');
    const colorImg = this.#getValidAttribute(swatch, 'data-color-image');
    const toneRaw = this.#getValidAttribute(swatch, 'data-swatch-tone');
    const title = this.#getValidAttribute(swatch, 'data-swatch-name');
    const description = this.#getValidAttribute(swatch, 'data-swatch-description');
    const variantId = this.#getValidAttribute(swatch, 'data-variant-id');
    const picker = this.#pickerRoot(swatch);
    const scope = picker?.dataset.showExampleScope || '';

    if (this.#titleEl) {
      this.#titleEl.textContent = title;
      this.#titleEl.hidden = !title;
    }

    if (this.#descEl) {
      this.#descEl.textContent = description;
      this.#descEl.hidden = !description;
    }

    const toneKey = this.#toneLabelClass(toneRaw);
    if (this.#toneEl) {
      this.#toneEl.textContent = toneKey;
      this.#toneEl.className = `swatch-option-tooltip__tone swatch-option-tooltip__tone--${toneKey}`;
    }
    if (this.#toneRowEl) this.#toneRowEl.hidden = !toneKey;

    if (this.#textEl) this.#textEl.hidden = !title && !toneKey;

    // Two layouts, picked from the colour's own data — never from the swatch's
    // position in the picker:
    //   colour HAS an example image → dual media (colour sample + example
    //     model shot) stacked above the copy.
    //   colour has NO example image → single colour sample beside the copy.
    // `sample_image` on the colour metaobject is the example/model shot and
    // `image` is the colour sample square (same pairing as the "see examples"
    // popup in sections/show-example-popup.liquid).
    const hasExample = Boolean(colorImg && sampleImg);

    // Fallback single image — prefer the colour sample, fall back to the
    // example or the variant image when the colour has no sample of its own.
    const singleSrc = colorImg || sampleImg || variantImg;

    // Wire each image's src. The image is kept visually hidden until its new
    // src has decoded so the previous swatch's picture never flashes.
    this.#applyImg(this.#imgPrimary, colorImg, hasExample);
    this.#applyImg(this.#imgSecondary, sampleImg, hasExample);
    this.#applyImg(this.#imgHero, singleSrc, !hasExample);
    // Legacy single-desktop frame is no longer used (replaced by dual).
    if (this.#imgDesktop) {
      this.#imgDesktop.removeAttribute('src');
      this.#imgDesktop.style.display = 'none';
    }

    if (this.#ctaBtn) {
      if (variantId) this.#ctaBtn.setAttribute('data-variant-id', variantId);
      else this.#ctaBtn.removeAttribute('data-variant-id');
      if (scope) this.#ctaBtn.setAttribute('data-show-example-scope', scope);
      else this.#ctaBtn.removeAttribute('data-show-example-scope');
    }

    const mobile = this.#isMobileViewport();
    this.#tooltip.classList.toggle('swatch-option-tooltip--mobile', mobile);
    this.#tooltip.classList.toggle('swatch-option-tooltip--desktop', !mobile);

    this.#tooltip.classList.toggle('swatch-option-tooltip--with-example', hasExample);
    this.#tooltip.classList.toggle('swatch-option-tooltip--no-example', !hasExample);

    // Show whichever media variant has content; hide the others.
    if (this.#desktopMedia) this.#desktopMedia.hidden = true;
    if (this.#rowDual) this.#rowDual.hidden = !hasExample;
    if (this.#rowSingle) this.#rowSingle.hidden = hasExample;
    if (this.#closeBtn) this.#closeBtn.hidden = true;
    if (this.#backdrop) this.#backdrop.hidden = !mobile;
    if (this.#ctaBtn) this.#ctaBtn.hidden = true;
  }

  /** @param {Element} swatch */
  #positionDesktop(swatch) {
    if (!this.#tooltip) return;

    // Measured inside the frame so the swatch rect and the popup's own size
    // come from the same layout pass — the popup is still being sized from
    // the colour that was just written into it.
    window.requestAnimationFrame(() => {
      if (!this.#tooltip) return;
      const { left, top } = swatchTooltipAnchoredPosition(
        swatch.getBoundingClientRect(),
        this.#tooltip.offsetWidth,
        this.#tooltip.offsetHeight
      );

      this.#tooltip.style.left = `${left}px`;
      this.#tooltip.style.top = `${top}px`;
      this.#tooltip.style.transform = '';
    });
  }

  /** @param {Element} swatch */
  #showTooltip(swatch) {
    if (!this.#tooltip || swatch.getAttribute('data-no-tooltip') === 'true') return;

    // Cancel any in-flight hide animation so a quick re-hover doesn't get
    // hidden mid-show by the pending display:none.
    if (this.#hideAnimTimer !== null) {
      clearTimeout(this.#hideAnimTimer);
      this.#hideAnimTimer = null;
    }

    this.#lastSwatch = swatch;
    this.#populateFromSwatch(swatch);
    const mobile = this.#isMobileViewport();
    // Start hidden + slightly offset, then fade & slide in. This smooths the
    // appearance and hides the brief flash of the previous swatch's image
    // while the new src loads. Fade is on the tooltip (incl. backdrop); slide
    // is on the surface so it never conflicts with #positionDesktop's transform.
    this.#tooltip.style.opacity = '0';
    this.#tooltip.style.transition = 'opacity 200ms ease';
    if (this.#surface) {
      this.#surface.style.transform = 'translateY(8px)';
      this.#surface.style.transition = 'transform 220ms cubic-bezier(0.16, 1, 0.3, 1)';
    }
    this.#tooltip.style.display = mobile ? 'flex' : 'block';
    this.#tooltip.setAttribute('aria-hidden', 'false');

    if (mobile) {
      this.#tooltip.style.position = '';
      this.#tooltip.style.left = '';
      this.#tooltip.style.top = '';
      this.#tooltip.style.right = '';
      this.#tooltip.style.bottom = '';
      this.#tooltip.style.width = '';
      this.#tooltip.style.height = '';
      this.#tooltip.style.zIndex = '';
      this.#attachDocTouchDismiss();
      this.#lockBodyScroll();
    } else {
      this.#unlockBodyScroll();
      this.#tooltip.style.position = 'absolute';
      this.#tooltip.style.zIndex = '9999';
      this.#detachDocTouchDismiss();
      this.#positionDesktop(swatch);
    }

    this.#fadeIn();
  }

  #fadeIn() {
    // Double rAF so the initial opacity:0 / translateY paint commits before
    // transitioning, otherwise the browser collapses both styles into one
    // frame and nothing animates.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!this.#tooltip || this.#tooltip.getAttribute('aria-hidden') !== 'false') return;
        this.#tooltip.style.opacity = '1';
        if (this.#surface) this.#surface.style.transform = 'translateY(0)';
      });
    });
  }

  #hideTooltip() {
    if (!this.#tooltip) return;
    this.#clearLongPress();
    this.#longPressFired = false;
    this.#unlockBodyScroll();
    this.#detachDocTouchDismiss();
    this.#lastSwatch = null;
    this.#tooltip.setAttribute('aria-hidden', 'true');

    // Already hidden — nothing to animate.
    if (this.#tooltip.style.display === 'none') return;

    // Animate out (fade + slide back down), then actually hide.
    this.#tooltip.style.transition = 'opacity 160ms ease';
    this.#tooltip.style.opacity = '0';
    if (this.#surface) {
      this.#surface.style.transition = 'transform 180ms cubic-bezier(0.4, 0, 1, 1)';
      this.#surface.style.transform = 'translateY(8px)';
    }

    if (this.#hideAnimTimer !== null) clearTimeout(this.#hideAnimTimer);
    this.#hideAnimTimer = window.setTimeout(() => {
      this.#hideAnimTimer = null;
      // A new show may have re-opened the tooltip; only hide if still closing.
      if (!this.#tooltip || this.#tooltip.getAttribute('aria-hidden') !== 'true') return;
      this.#tooltip.style.display = 'none';
      this.#tooltip.style.opacity = '';
      this.#tooltip.style.transition = '';
      if (this.#surface) {
        this.#surface.style.transform = '';
        this.#surface.style.transition = '';
      }
    }, 180);
  }

  #attachDocTouchDismiss() {
    if (this.#boundDocTouch) return;
    this.#boundDocTouch = (/** @type {TouchEvent} */ e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (this.#tooltip?.contains(t)) return;
      if (t instanceof Element && t.closest('.color-swatch')) return;
      this.#hideTooltip();
    };
    document.addEventListener('touchstart', this.#boundDocTouch, { passive: true });
  }

  #detachDocTouchDismiss() {
    if (!this.#boundDocTouch) return;
    document.removeEventListener('touchstart', this.#boundDocTouch);
    this.#boundDocTouch = null;
  }

  #bindEvents() {
    document.querySelectorAll('.color-swatch').forEach((swatch) => {
      if (swatch.getAttribute('data-no-tooltip') === 'true') return;

      swatch.addEventListener('mouseenter', () => {
        if (this.#isMobileViewport()) return;
        this.#showTooltip(swatch);
      });
      swatch.addEventListener('mouseleave', () => {
        if (this.#isMobileViewport()) return;
        this.#hideTooltip();
      });
      swatch.addEventListener('focus', () => {
        if (this.#isMobileViewport()) return;
        this.#showTooltip(swatch);
      });
      swatch.addEventListener('blur', () => {
        if (this.#isMobileViewport()) return;
        this.#hideTooltip();
      });
      swatch.addEventListener(
        'touchstart',
        (e) => {
          if (!this.#isMobileViewport()) return;
          e.stopPropagation();
          this.#longPressFired = false;
          this.#clearLongPress();
          this.#longPressTimer = window.setTimeout(() => {
            this.#longPressFired = true;
            this.#showTooltip(swatch);
          }, 500);
        },
        { passive: true }
      );
      swatch.addEventListener('touchend', () => this.#clearLongPress(), { passive: true });
      swatch.addEventListener('touchmove', () => this.#clearLongPress(), { passive: true });
      swatch.addEventListener('touchcancel', () => this.#clearLongPress(), { passive: true });
      swatch.addEventListener('contextmenu', (e) => {
        if (this.#isMobileViewport()) e.preventDefault();
      });
      swatch.addEventListener('click', (e) => {
        if (this.#longPressFired) {
          e.preventDefault();
          e.stopPropagation();
          this.#longPressFired = false;
        }
      });
    });

    document.querySelectorAll('.variant-option__button-label input').forEach((input) => {
      if (input.getAttribute('data-no-tooltip') === 'true') return;
      const swatch = this.#resolveSwatchButton(input);
      if (!swatch) return;

      input.addEventListener('mouseenter', () => {
        if (this.#isMobileViewport()) return;
        this.#showTooltip(swatch);
      });
      input.addEventListener('mouseleave', () => {
        if (this.#isMobileViewport()) return;
        this.#hideTooltip();
      });
      input.addEventListener('focus', () => {
        if (this.#isMobileViewport()) return;
        this.#showTooltip(swatch);
      });
      input.addEventListener('blur', () => {
        if (this.#isMobileViewport()) return;
        this.#hideTooltip();
      });
    });

    window.addEventListener('resize', () => {
      if (!this.#tooltip || this.#tooltip.style.display === 'none') return;
      if (!this.#lastSwatch) return;

      const mobile = this.#isMobileViewport();
      this.#populateFromSwatch(this.#lastSwatch);

      if (mobile) {
        this.#tooltip.style.display = 'flex';
        this.#tooltip.style.position = '';
        this.#tooltip.style.left = '';
        this.#tooltip.style.top = '';
        this.#tooltip.style.right = '';
        this.#tooltip.style.bottom = '';
        this.#tooltip.style.width = '';
        this.#tooltip.style.height = '';
        this.#tooltip.style.zIndex = '';
        this.#attachDocTouchDismiss();
        this.#lockBodyScroll();
      } else {
        this.#unlockBodyScroll();
        this.#tooltip.style.display = 'block';
        this.#tooltip.style.position = 'absolute';
        this.#tooltip.style.right = '';
        this.#tooltip.style.bottom = '';
        this.#tooltip.style.width = '';
        this.#tooltip.style.height = '';
        this.#tooltip.style.zIndex = '9999';
        this.#detachDocTouchDismiss();
        this.#positionDesktop(this.#lastSwatch);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#tooltip?.getAttribute('aria-hidden') === 'false') {
        this.#hideTooltip();
      }
    });
  }
}

new SwatchTooltip();

// Consultation swatch tooltip — Figma 8085:48652
// Shown on hover/focus of `.color-swatch--consultation` (the "?" button)
class ConsultationTooltip {
  /** @type {HTMLElement | null} */
  #tooltip = null;
  /** @type {HTMLElement | null} */
  #backdrop = null;
  /** @type {HTMLElement | null} */
  #surface = null;
  /** @type {HTMLButtonElement | null} */
  #closeBtn = null;
  /** @type {HTMLButtonElement | null} */
  #ctaBtn = null;
  /** @type {HTMLElement | null} */
  #titleEl = null;
  /** @type {HTMLElement | null} */
  #descEl = null;
  /** @type {Element | null} */
  #lastSwatch = null;
  /** @type {number | null} */
  #hideTimer = null;
  /** @type {(() => void) | null} */
  #boundDocTouch = null;
  /** @type {boolean} */
  #bodyScrollLocked = false;
  /** @type {string} */
  #savedBodyOverflow = '';
  /** @type {number | null} */
  #longPressTimer = null;
  /** @type {boolean} */
  #longPressFired = false;

  constructor() {
    this.#build();
    this.#bindEvents();
  }

  #build() {
    this.#tooltip = document.createElement('div');
    this.#tooltip.className = 'swatch-consultation-tooltip';
    this.#tooltip.setAttribute('role', 'dialog');
    this.#tooltip.setAttribute('aria-hidden', 'true');
    this.#tooltip.innerHTML = `
      <div class="swatch-consultation-tooltip__backdrop" aria-hidden="true"></div>
      <div class="swatch-consultation-tooltip__surface">
        <button type="button" class="swatch-consultation-tooltip__close" aria-label="Close">Close</button>
        <p class="swatch-consultation-tooltip__title"></p>
        <div class="swatch-consultation-tooltip__description"></div>
        <button type="button" class="swatch-consultation-tooltip__cta">Add consultation request</button>
      </div>
    `;
    document.body.appendChild(this.#tooltip);
    this.#tooltip.style.display = 'none';

    this.#backdrop = this.#tooltip.querySelector('.swatch-consultation-tooltip__backdrop');
    this.#surface = this.#tooltip.querySelector('.swatch-consultation-tooltip__surface');
    this.#closeBtn = this.#tooltip.querySelector('.swatch-consultation-tooltip__close');
    this.#ctaBtn = this.#tooltip.querySelector('.swatch-consultation-tooltip__cta');
    this.#titleEl = this.#tooltip.querySelector('.swatch-consultation-tooltip__title');
    this.#descEl = this.#tooltip.querySelector('.swatch-consultation-tooltip__description');

    this.#closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#hideTooltip();
    });

    this.#ctaBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#selectConsultation();
      this.#hideTooltip();
    });
    this.#backdrop?.addEventListener('click', () => this.#hideTooltip());

    // Keep tooltip open when pointer moves from swatch onto surface
    this.#surface?.addEventListener('mouseenter', () => this.#cancelHide());
    this.#surface?.addEventListener('mouseleave', () => {
      if (!this.#usesModalPresentation()) this.#scheduleHide();
    });
  }

  /**
   * The anchored tooltip is hover-driven, which a coarse pointer cannot drive:
   * on a touch device `mouseenter` only fires after a tap and the matching
   * `mouseleave` may never arrive, leaving the tooltip stuck over the header.
   * So any touch device gets the modal presentation, not just narrow viewports
   * — tablets at 750-1199px are touch but wider than the mobile breakpoint.
   */
  /**
   * Picks the consultation option the popup describes. It drives the option
   * <select> and fires `change`, the same path the picker's own swatch click
   * takes — rather than calling `.click()` on the swatch, which would be seen
   * by the delegated open-handler and immediately reopen this modal.
   */
  #selectConsultation() {
    const swatch = this.#lastSwatch;
    const value = swatch?.getAttribute('data-option-value');
    if (!value) return;
    const picker = swatch.closest('[data-pdp-variant-swatch-picker]');
    const select = picker?.querySelector('.variant-option--dropdowns select');
    if (!select) return;
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #usesModalPresentation() {
    return (
      window.matchMedia(`(max-width: ${SWATCH_TOOLTIP_MOBILE_MAX}px)`).matches ||
      window.matchMedia('(hover: none) and (pointer: coarse)').matches
    );
  }

  #lockBodyScroll() {
    if (this.#bodyScrollLocked) return;
    this.#savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.#bodyScrollLocked = true;
  }

  #unlockBodyScroll() {
    if (!this.#bodyScrollLocked) return;
    document.body.style.overflow = this.#savedBodyOverflow;
    this.#bodyScrollLocked = false;
    this.#savedBodyOverflow = '';
  }

  #cancelHide() {
    if (this.#hideTimer !== null) {
      clearTimeout(this.#hideTimer);
      this.#hideTimer = null;
    }
  }

  #scheduleHide() {
    this.#cancelHide();
    this.#hideTimer = window.setTimeout(() => this.#hideTooltip(), 120);
  }

  #clearLongPress() {
    if (this.#longPressTimer !== null) {
      clearTimeout(this.#longPressTimer);
      this.#longPressTimer = null;
    }
  }

  /** @param {Element} swatch */
  #populateFromSwatch(swatch) {
    // Figma titles the popup "Expert Consultation" and carries the whole of the
    // copy as the body, so the opening line moves out of the heading slot.
    const defaultTitle = 'Expert Consultation';
    const defaultDesc =
      "Not sure on color or style? We\u2019ve got you. " +
      "Select this option to book a free consultation with our cosmetologist after checkout. " +
      "You can send us a photo or hair swatch, and we\u2019ll help you find your perfect match.";

    if (this.#titleEl) this.#titleEl.textContent = defaultTitle;
    if (this.#descEl) this.#descEl.textContent = defaultDesc;

    const mobile = this.#usesModalPresentation();
    this.#tooltip.classList.toggle('swatch-consultation-tooltip--mobile', mobile);
    this.#tooltip.classList.toggle('swatch-consultation-tooltip--desktop', !mobile);
    if (this.#backdrop) this.#backdrop.hidden = !mobile;
    if (this.#closeBtn) this.#closeBtn.hidden = !mobile;
    // Figma shows the CTA on the mobile modal only; the desktop tooltip is a
    // hover popup with nothing to confirm.
    if (this.#ctaBtn) this.#ctaBtn.hidden = !mobile;
  }

  /** @param {Element} swatch */
  #positionDesktop(swatch) {
    if (!this.#tooltip) return;

    // Measured inside the frame so the swatch rect and the popup's own size
    // come from the same layout pass — the popup is still being sized from
    // the colour that was just written into it.
    window.requestAnimationFrame(() => {
      if (!this.#tooltip) return;
      const { left, top } = swatchTooltipAnchoredPosition(
        swatch.getBoundingClientRect(),
        this.#tooltip.offsetWidth,
        this.#tooltip.offsetHeight
      );

      this.#tooltip.style.left = `${left}px`;
      this.#tooltip.style.top = `${top}px`;
      this.#tooltip.style.transform = '';
    });
  }

  /** @param {Element} swatch */
  #showTooltip(swatch) {
    if (!this.#tooltip) return;
    this.#cancelHide();
    this.#lastSwatch = swatch;
    this.#populateFromSwatch(swatch);

    const mobile = this.#usesModalPresentation();
    this.#tooltip.style.display = mobile ? 'flex' : 'block';
    this.#tooltip.setAttribute('aria-hidden', 'false');

    if (mobile) {
      this.#tooltip.style.position = '';
      this.#tooltip.style.left = '';
      this.#tooltip.style.top = '';
      this.#tooltip.style.zIndex = '';
      this.#attachDocTouchDismiss();
      this.#lockBodyScroll();
    } else {
      this.#unlockBodyScroll();
      this.#tooltip.style.position = 'absolute';
      this.#tooltip.style.zIndex = '9999';
      this.#detachDocTouchDismiss();
      this.#positionDesktop(swatch);
    }
  }

  #hideTooltip() {
    if (!this.#tooltip) return;
    this.#cancelHide();
    this.#clearLongPress();
    this.#longPressFired = false;
    this.#unlockBodyScroll();
    this.#tooltip.style.display = 'none';
    this.#tooltip.setAttribute('aria-hidden', 'true');
    this.#lastSwatch = null;
    this.#detachDocTouchDismiss();
  }

  #attachDocTouchDismiss() {
    if (this.#boundDocTouch) return;
    this.#boundDocTouch = (/** @type {TouchEvent} */ e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (this.#tooltip?.contains(t)) return;
      if (t instanceof Element && t.closest('.color-swatch--consultation')) return;
      this.#hideTooltip();
    };
    document.addEventListener('touchstart', this.#boundDocTouch, { passive: true });
  }

  #detachDocTouchDismiss() {
    if (!this.#boundDocTouch) return;
    document.removeEventListener('touchstart', this.#boundDocTouch);
    this.#boundDocTouch = null;
  }

  #bindEvents() {
    document.querySelectorAll('.color-swatch--consultation').forEach((swatch) => {
      swatch.addEventListener('mouseenter', () => {
        if (this.#usesModalPresentation()) return;
        this.#showTooltip(swatch);
      });
      swatch.addEventListener('mouseleave', () => {
        if (this.#usesModalPresentation()) return;
        this.#scheduleHide();
      });
      swatch.addEventListener('focus', () => {
        if (this.#usesModalPresentation()) return;
        this.#showTooltip(swatch);
      });
      swatch.addEventListener('blur', () => {
        if (this.#usesModalPresentation()) return;
        this.#scheduleHide();
      });
      swatch.addEventListener(
        'touchstart',
        (e) => {
          if (!this.#usesModalPresentation()) return;
          e.stopPropagation();
          this.#longPressFired = false;
          this.#clearLongPress();
          this.#longPressTimer = window.setTimeout(() => {
            this.#longPressFired = true;
            this.#showTooltip(swatch);
          }, 500);
        },
        { passive: true }
      );
      swatch.addEventListener('touchend', () => this.#clearLongPress(), { passive: true });
      swatch.addEventListener('touchmove', () => this.#clearLongPress(), { passive: true });
      swatch.addEventListener('touchcancel', () => this.#clearLongPress(), { passive: true });
      swatch.addEventListener('contextmenu', (e) => {
        if (this.#usesModalPresentation()) e.preventDefault();
      });
      swatch.addEventListener('click', (e) => {
        if (this.#longPressFired) {
          e.preventDefault();
          e.stopPropagation();
          this.#longPressFired = false;
        }
      });
    });

    // Touch devices have no hover, so a plain tap opens the info modal. The
    // variant is still selected: this neither prevents the default nor stops
    // propagation, so the picker's own click handler runs as before.
    // Delegated from the document so it survives the picker re-rendering on
    // variant change, which the per-swatch listeners above do not.
    document.addEventListener('click', (e) => {
      if (!this.#usesModalPresentation()) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      const swatch = target.closest('.color-swatch--consultation');
      if (!swatch) return;
      // A long press already opened it and swallowed this click.
      if (this.#longPressFired) {
        this.#longPressFired = false;
        return;
      }
      this.#showTooltip(swatch);
    });

    window.addEventListener('resize', () => {
      if (!this.#tooltip || this.#tooltip.style.display === 'none') return;
      if (!this.#lastSwatch) return;

      const mobile = this.#usesModalPresentation();
      this.#populateFromSwatch(this.#lastSwatch);

      if (mobile) {
        this.#tooltip.style.display = 'flex';
        this.#tooltip.style.position = '';
        this.#tooltip.style.left = '';
        this.#tooltip.style.top = '';
        this.#tooltip.style.zIndex = '';
        this.#attachDocTouchDismiss();
        this.#lockBodyScroll();
      } else {
        this.#unlockBodyScroll();
        this.#tooltip.style.display = 'block';
        this.#tooltip.style.position = 'absolute';
        this.#tooltip.style.zIndex = '9999';
        this.#detachDocTouchDismiss();
        this.#positionDesktop(this.#lastSwatch);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#tooltip?.getAttribute('aria-hidden') === 'false') {
        this.#hideTooltip();
      }
    });
  }
}

new ConsultationTooltip();
